/**
 * Timesheet Deadline Settings Component
 * Allows configuration of organization-wide submission deadlines
 */

import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from 'lucide-react';
import type { TimesheetDeadlineSettings as DeadlineSettings } from '@/types/timesheetSettings';

const REMINDER_DAYS_MIN = 1;
const REMINDER_DAYS_MAX = 14;

interface TimesheetDeadlineSettingsProps {
  settings: DeadlineSettings;
  onChange?: (settings: DeadlineSettings, isValid: boolean) => void;
  onSave?: (settings: DeadlineSettings) => void;
  onNavigateToNotifications?: () => void;
}

export const TimesheetDeadlineSettings: React.FC<TimesheetDeadlineSettingsProps> = ({
  settings,
  onChange,
  onSave,
  onNavigateToNotifications
}) => {
  const [localSettings, setLocalSettings] = useState<DeadlineSettings>(settings);
  const [reminderDaysError, setReminderDaysError] = useState<string | null>(null);

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      const reminderDaysValid =
        localSettings.reminderDaysBefore >= REMINDER_DAYS_MIN &&
        localSettings.reminderDaysBefore <= REMINDER_DAYS_MAX;

      const isValid =
        localSettings.deadlineTime !== '' &&
        localSettings.gracePeriodDays >= 0 &&
        localSettings.gracePeriodDays <= 7 &&
        reminderDaysValid;

      onChange(localSettings, isValid);
    }
  }, [localSettings, onChange]);

  // Update local state when prop changes (for reset functionality)
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleChange = (field: keyof DeadlineSettings, value: any) => {
    setLocalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReminderDaysChange = (raw: string) => {
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) {
      setReminderDaysError(`Please enter a value between ${REMINDER_DAYS_MIN} and ${REMINDER_DAYS_MAX}.`);
      setLocalSettings(prev => ({ ...prev, reminderDaysBefore: 0 }));
    } else if (parsed < REMINDER_DAYS_MIN || parsed > REMINDER_DAYS_MAX) {
      setReminderDaysError(`Please enter a value between ${REMINDER_DAYS_MIN} and ${REMINDER_DAYS_MAX}.`);
      setLocalSettings(prev => ({ ...prev, reminderDaysBefore: parsed }));
    } else {
      setReminderDaysError(null);
      setLocalSettings(prev => ({ ...prev, reminderDaysBefore: parsed }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-start gap-3 pb-4 border-b">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <Calendar className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold">Submission Deadlines</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure when timesheets are due and grace period rules
          </p>
        </div>
      </div>

      {/* Deadline Configuration - Three Column Layout */}
      <div className="p-4 bg-muted/20 rounded-lg border space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Deadline Day */}
          <div className="space-y-2">
            <Label htmlFor="deadline-day" className="text-sm font-medium">
              Deadline Day
            </Label>
            <Select
              value={localSettings.deadlineDay.toString()}
              onValueChange={(value) => handleChange('deadlineDay', parseInt(value) as 0 | 1 | 2 | 3 | 4 | 5 | 6)}
            >
              <SelectTrigger id="deadline-day" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Sunday</SelectItem>
                <SelectItem value="1">Monday</SelectItem>
                <SelectItem value="2">Tuesday</SelectItem>
                <SelectItem value="3">Wednesday</SelectItem>
                <SelectItem value="4">Thursday</SelectItem>
                <SelectItem value="5">Friday</SelectItem>
                <SelectItem value="6">Saturday</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Which day of the week must timesheets be submitted by
            </p>
          </div>

          {/* Deadline Time */}
          <div className="space-y-2">
            <Label htmlFor="deadline-time" className="text-sm font-medium">
              Deadline Time
            </Label>
            <Input
              id="deadline-time"
              type="time"
              value={localSettings.deadlineTime}
              onChange={(e) => handleChange('deadlineTime', e.target.value)}
              className="h-10"
            />
            <p className="text-xs text-muted-foreground">
              Time on the deadline day when timesheets are due
            </p>
          </div>

          {/* Grace Period */}
          <div className="space-y-2">
            <Label htmlFor="grace-period" className="text-sm font-medium">
              Grace Period
            </Label>
            <div className="flex gap-2">
              <Input
                id="grace-period"
                type="number"
                min="0"
                max="7"
                value={localSettings.gracePeriodDays}
                onChange={(e) => handleChange('gracePeriodDays', parseInt(e.target.value) || 0)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    handleChange('gracePeriodDays', Math.min(7, localSettings.gracePeriodDays + 1));
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    handleChange('gracePeriodDays', Math.max(0, localSettings.gracePeriodDays - 1));
                  }
                }}
                className="h-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <div className="flex items-center px-3 text-sm text-muted-foreground whitespace-nowrap">
                days
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Days after deadline before marking as "late"
            </p>
          </div>

          {/* Reminder Days Before */}
          <div className="space-y-2">
            <Label htmlFor="reminder-days-before" className="text-sm font-medium">
              Reminder Days Before
            </Label>
            <div className="flex gap-2">
              <Input
                id="reminder-days-before"
                type="number"
                min={REMINDER_DAYS_MIN}
                max={REMINDER_DAYS_MAX}
                value={localSettings.reminderDaysBefore}
                onChange={(e) => handleReminderDaysChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    handleReminderDaysChange(String(Math.min(REMINDER_DAYS_MAX, localSettings.reminderDaysBefore + 1)));
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    handleReminderDaysChange(String(Math.max(REMINDER_DAYS_MIN, localSettings.reminderDaysBefore - 1)));
                  }
                }}
                className={`h-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none${reminderDaysError ? ' border-destructive focus-visible:ring-destructive' : ''}`}
                aria-describedby={reminderDaysError ? 'reminder-days-error' : 'reminder-days-hint'}
                aria-invalid={!!reminderDaysError}
              />
              <div className="flex items-center px-3 text-sm text-muted-foreground whitespace-nowrap">
                days
              </div>
            </div>
            {reminderDaysError ? (
              <p id="reminder-days-error" className="text-xs text-destructive">
                {reminderDaysError}
              </p>
            ) : (
              <p id="reminder-days-hint" className="text-xs text-muted-foreground">
                How many days before the deadline to send the feed reminder
              </p>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};
